

import React, { useContext, useState } from 'react'
import { GlobalContext } from '../GlobalContext'
import CloudformationOutputs from '../CloudformationOutputs.json'
import Modal from '../ui-elements/Modal'
import JsonDebug from '../ui-elements/JsonDebug'

type Props = {
  questionAnswer: any
  isTemperature?: boolean
  size?: 'normal' | 'big'
  question?: any
}


function FormatLocationAnswer({
  location,
  classString = ''
}: any) {

  const manuallyEnteredLocation = location.locationManuallyEntered
  const locationFromUser = location.locationFromUser
  const locationFromGps = location.locationFromGps
  const locationAsString = location.locationAsString
  let latFromUser = locationFromUser ? locationFromUser.lat : null
  let lngFromUser = locationFromUser ? locationFromUser.lng : null
  let latFromGps = locationFromGps ? locationFromGps.lat : null
  let lngFromGps = locationFromGps ? locationFromGps.lng : null
  let lat = latFromUser || latFromGps || null
  let lng = lngFromUser || lngFromGps || null
  let latStr = lat ? parseFloat(lat as any).toFixed(5) : ''
  let lngStr = lng ? parseFloat(lng as any).toFixed(5) : ''
  const coordinates = latStr && lngStr ? `Latitude: ${latStr}, Longitude: ${lngStr}` : null

  const doCoordinatesFromUserMatchGPS = latFromUser && lngFromUser && latFromGps && lngFromGps && (latFromUser === latFromGps) && (lngFromUser === lngFromGps)



  const { t } = useContext(GlobalContext)
  if (manuallyEnteredLocation) {
    return <>
      <span className={`text-xl`}>📍</span>
      <span className={classString}>
        {manuallyEnteredLocation}
      </span>
      <span className={`text-xs text-gray-600 italic font-default pl-1`}>
        {t('Dashboard.FormatQuestionAnswer.manuallyEntered')}
      </span>
    </>
  }


  return <>
    <span className={`text-xl`}>📍</span>
    <span className={classString}>
      {locationAsString ?
        `${locationAsString}` :
        `${coordinates || manuallyEnteredLocation}`
      }
    </span>
    <span className={`text-xs text-gray-600 italic font-default pl-1`}>
      {locationFromGps && locationFromUser && !doCoordinatesFromUserMatchGPS ? t('Dashboard.FormatQuestionAnswer.locatedByUser') : t('Dashboard.FormatQuestionAnswer.fromDeviceGPS')}
    </span>
  </>
}


function FormatQuestionAnswer({
  questionAnswer,
  isTemperature = false,
  size = 'normal',
  question
}: Props) {

  const {
    tableData,
    currentOrganisation
  } = useContext(GlobalContext)

  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const isQuestionSkipped = questionAnswer && !questionAnswer.Answer && questionAnswer.AnswerSkipped
  const isQuestionPhoto = question && question.QuestionFormat === 'Photo'
  const isNoAnswerValue = !questionAnswer || !questionAnswer.Answer || Object.keys(questionAnswer.Answer).length === 0 || JSON.stringify(questionAnswer.Answer) === '{"NULL":true}'
  const isLocationAnswer = questionAnswer.Answer instanceof Object && !(questionAnswer.Answer instanceof Array) && (questionAnswer.Answer.locationFromGps || questionAnswer.Answer.locationManuallyEntered)

  const { t } = useContext(GlobalContext)
  const classString = `
  ${size === 'normal' ? ' text-sm ' : ''}
  ${size === 'big' ? ' text-xl font-righteous text-brandblue ' : ''}
  `

  let unit = ''
  if (question && question.MeasurementUnit) {
    unit = ` ${question.MeasurementUnit}`
  } else if (isTemperature) {
    unit = ' °C'
  }

  function isValidIsoDateTime(str: string) {
    const regex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
    const match = str.match(regex)
    if (!match) return false
    const [_, year, month, day, hour, minute] = match.map(Number)
    const date = new Date(year, month - 1, day, hour, minute)
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day &&
      date.getHours() === hour &&
      date.getMinutes() === minute
    )
  }

  function formatAnswerContent(answerContent: any): string {

    // handle strings (possibly with date-time, posisbly numbers as strings so need to include units if they exist)
    if (typeof answerContent === "string") {
      if (isValidIsoDateTime(answerContent)) {
        return `${answerContent.replace('T', ' ')}${unit}`
      } else {
        return `${answerContent}${unit}`
      }
    }

    // handle numbers and (optional) units
    if (typeof answerContent === "number") {
      return `${answerContent}${unit}`
    }


    // Handle arrays
    if (Array.isArray(answerContent)) {
      return answerContent
        .map(item => formatAnswerContent(item)) // recursively format each item
        .join(", ")
        .concat(".")
    } 

    // Handle objects (and make sure it's not null)
    if (answerContent && typeof answerContent === "object") {
      return Object.entries(answerContent)
        .map(([key, value]) => `${key}: ${formatAnswerContent(value)}`)
        .join(". ")
    }

    // Fallback for anything else (e.g. null, undefined, boolean)
    return String(answerContent)
  }



  if (isQuestionPhoto || isNoAnswerValue) {
    return null
  }

  if (isQuestionSkipped) {
    return <span className={classString}>
      {t('Dashboard.FormatQuestionAnswer.questionSkipped')}
    </span>
  }

  if (isLocationAnswer) {
    return <FormatLocationAnswer
      classString={classString}
      location={questionAnswer.Answer}
    />
  }

  return <>
    <span className={classString}>
      {formatAnswerContent(questionAnswer.Answer)}
    </span>



    {questionAnswer.isAnswerManuallyEdited ? <span className={`text-xs text-gray-600 italic font-default pl-1`}>
      {t('Dashboard.FormatQuestionAnswer.manuallyEdited')}
    </span> : ''}
  </>

}

export default FormatQuestionAnswer